import { observer } from 'mobx-react';
import { FC, FormEvent } from 'react';
import BackNextButtons from 'components/BackNextButtons';
import ContactInformation, { ContactInformationFormutil } from 'components/forms/ContactInformation';
import DeliveryAddress, { USADeliveryAddressFormutil } from 'components/forms/DeliveryAddress/USA';
import { UsaHomeAddressFormutil } from 'components/forms/HomeAddress';
import HomeAddress from 'components/forms/HomeAddress/UsaHomeAddress';
import { usePowerNavigate } from 'components/PowerNavigate';
import Toast from 'components/Toast';
import { useCheckUSAPostal } from 'hooks/useCheckPostal';
import security from 'stores/security';
import { reportEventList } from 'utils/gtag';
import { setEmail, setPhone } from 'utils/request';
import withProgressForm, { ProgressFormProps } from 'utils/withProgressForm';
import styles from './style.scss';

const Address: FC<
    ProgressFormProps<ContactInformationFormutil & USADeliveryAddressFormutil & UsaHomeAddressFormutil>
> = ({ onSubmit, $formutil }) => {
    const navigate = usePowerNavigate();
    const { checkPostalMatchStateCodeError } = useCheckUSAPostal();
    const { email, phone, telCode } = security.userInfo;
    const {
        same_delivery_and_residence: formSameDeliveryAndResidence,
        verify_code,
        email: formEmail,
        tel_code: formTelCode,
        phone: formPhone
    } = $formutil.$params;

    const handleSubmit = (ev: FormEvent) => {
        onSubmit(async () => {
            Toast.loading(true);

            try {
                reportEventList('contact');

                if (!email && formEmail && verify_code) {
                    await setEmail({
                        email: formEmail,
                        verify_code
                    });

                    await security.getStatus();
                }

                if (!phone && formPhone && verify_code && formTelCode) {
                    await setPhone({
                        phone: formPhone,
                        tel_code: formTelCode,
                        verify_code
                    });

                    await security.getStatus();
                }

                // 针对marsco帐户 校验地址和邮编是否匹配
                const homePostalError = await checkPostalMatchStateCodeError(
                    'home_postal',
                    'home_state_code',
                    'home_address_type'
                );
                const deliveryPostalError = await checkPostalMatchStateCodeError(
                    'delivery_postal',
                    'delivery_state_code',
                    'delivery_address_type'
                );

                if (!homePostalError && !deliveryPostalError) {
                    navigate('../job');
                }
            } catch (error) {
                Toast.show(error);
            }

            Toast.loading(false);
        }, ev);
    };

    return (
        <form noValidate className={styles.root} onSubmit={handleSubmit}>
            <h3 className="open-title mt-0">{__('CONTACT AND ADDRESS INFORMATION')}</h3>
            <ContactInformation
                emailOptions={{
                    isPreview: !!email,
                    defaultValue: email
                }}
                phoneOptions={{
                    isPreview: !!phone,
                    defaultPhone: phone,
                    defaultTelCode: telCode
                }}
            />
            <h3 className="open-title mt-0">
                {__('RESIDENTIAL ADDRESS')}
                <div className={styles.tips}>
                    <p className="mb-0">{__("We're legally required to collect this information.")}</p>
                    <p className="mb-0">
                        {__('This is usually the address on your government-issued ID and cannot be a PO box.')}
                    </p>
                    <p>* {__('A PO Box can be used as a mailing address, but not residential address')}</p>
                </div>
            </h3>
            <HomeAddress />
            {formSameDeliveryAndResidence === '0' && <DeliveryAddress isSSN isMarsco countryDisabled />}
            <BackNextButtons sticky />
        </form>
    );
};

export default withProgressForm({
    step: 2,
    id: 'address',
    basePath: 'open',
    ensure: 'account',
    showProgressBar: true,
    eventParams: {
        exposureEventParams: {
            model: '开户-地址信息',
            name: '地址信息-页面曝光'
        },
        submitEventParams: {
            model: '开户-地址信息',
            name: '地址信息-点击下一步'
        }
    }
})(observer(Address));
